import styled from 'styled-components'
import { TdsButton, TdsIcon, TdsTooltip } from '@scania/tegel-react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { BreakpointWidthPx } from '../../../css/BreakpointWidthPx'
import {
  getGuidedViewFirstTimeAllView,
  getGuidedViewLoadTime,
  getGuidedViewPage,
  setGuidedViewFirstTimeAllView,
} from '../../../store/sessionDataSlice'
import { GuidedViewPageType } from '../../../store/types'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import { BreakpointHeightPx } from '../../../css/BreakpointHeightPx'
import {
  getMenuState,
  setMenuIdToOpenAtJumpToAllView,
} from '../../../store/menuSlice'
import { Size2d } from '../../../types/Size2d'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingTimeSpentInGuidedViewEvent,
} from '../../../utils/adobeAnalytics'
import { getMarketLanguageState } from '../../../store/appSlice'
import {
  buildRelativeAllViewPage,
  buildRelativeGvPage,
} from '../../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { getGvOpenMenuEtelFamily } from '../../../utils/getGvOpenMenuEtelFamily'

const ViewAllOptionsWrapper = styled.div`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: flex;
    justify-content: end;
    margin: 0px;
  }
`

interface ViewAllOrLessOptionsButtonProps {
  parentSize: Size2d
}

export function ViewAllOrLessOptionsButton({
  parentSize,
}: ViewAllOrLessOptionsButtonProps): JSX.Element {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const firstTimeVisitingAllView = useAppSelector(getGuidedViewFirstTimeAllView)
  const guidedViewLoadTime = useAppSelector(getGuidedViewLoadTime)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  const menuTreeGv = useAppSelector(getMenuState).menuResultGv?.menuTree

  const handleAllOrLessOptionsClick = () => {
    if (currentlyLoadingId) {
      console.log(
        'Ignored a button click while waiting for a pending config change.',
      )
      return
    }
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    if (guidedViewPage === GuidedViewPageType.guidedView) {
      const relativeUrl = buildRelativeAllViewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
      const menuIdToOpenAtJumpToAllView = getGvOpenMenuEtelFamily(menuTreeGv)
      dispatch(setMenuIdToOpenAtJumpToAllView(menuIdToOpenAtJumpToAllView))

      if (firstTimeVisitingAllView && guidedViewLoadTime) {
        const currentTime = Date.now()
        const trackingEvent: ScaniaAdobeTrackingTimeSpentInGuidedViewEvent = {
          event: ScaniaAdobeEventId.GuidedViewTimeSeconds,
          eventInfo: {
            timeSpentUntilAllView: (currentTime - guidedViewLoadTime) / 1000,
          },
        }
        pushAdobeEvent(trackingEvent)
        dispatch(setGuidedViewFirstTimeAllView(false))
      }
    }
    if (guidedViewPage === GuidedViewPageType.allView) {
      const relativeUrl = buildRelativeGvPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    }
  }

  return (
    <ViewAllOptionsWrapper>
      <TdsTooltip
        selector="#viewAllOrLessButton"
        text={t2(TextId.DESCRIPTION_VIEW_ALL)}
      />
      <TdsButton
        id="viewAllOrLessButton"
        variant={
          parentSize.width < BreakpointWidthPx.Small ? 'ghost' : 'secondary'
        }
        text={
          guidedViewPage === GuidedViewPageType.guidedView
            ? t2(TextId.ACTION_LABEL_VIEW_ALL_OPTIONS)
            : t2(TextId.ACTION_LABEL_VIEW_LESS)
        }
        size="sm"
        onClick={handleAllOrLessOptionsClick}
      >
        <TdsIcon slot="icon" size="16px" name="filters" />
      </TdsButton>
    </ViewAllOptionsWrapper>
  )
}
